/**
 * `realtimeAudio.ts` — browser audio halves of Unity's
 * `AIAgentTransportWebSocketAudio.cs`.
 *
 * Mic capture: getUserMedia → AudioWorklet → downsample to 24 kHz mono
 * PCM16 → base64 chunks (~50 ms cadence, mirroring Unity's mic pump).
 * Playback: base64 PCM16 24 kHz deltas scheduled back-to-back on an
 * AudioContext timeline (Unity's ring-buffer AudioSource equivalent).
 * On mute we append ~800 ms of silence so server VAD closes the user turn
 * (Unity parity: `ServerVADSilenceDurationMs + 300`).
 */

import { Emitter } from "@core/events/emitter";

export const REALTIME_SAMPLE_RATE = 24000;
/** Mic pump cadence (samples per flush at 24 kHz ≈ 50 ms). */
const MIC_FLUSH_SAMPLES = 1200;
const MUTE_SILENCE_MS = 800;

/**
 * Microphone constraints, shared by BOTH transports so they cannot drift.
 *
 * `echoCancellation` is the one that bites: both transports render the agent's
 * audio to the speakers, so a mic captured without AEC feeds the agent back
 * into itself and it interrupts its own answer.
 */
export const K_REALTIME_MIC_CONSTRAINTS: MediaTrackConstraints = {
  channelCount: 1,
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
};

// Inline AudioWorklet that forwards raw Float32 mono frames to the main thread.
const CAPTURE_WORKLET_SOURCE = `
class CampfireCaptureProcessor extends AudioWorkletProcessor {
  process(inputs) {
    const channel = inputs[0] && inputs[0][0];
    if (channel && channel.length > 0) {
      this.port.postMessage(channel.slice(0));
    }
    return true;
  }
}
registerProcessor("campfire-capture", CampfireCaptureProcessor);
`;

export class RealtimeMicCapture {
  private m_context: AudioContext | null = null;
  private m_stream: MediaStream | null = null;
  private m_source: MediaStreamAudioSourceNode | null = null;
  private m_worklet: AudioWorkletNode | null = null;
  private m_pending: number[] = [];
  private m_muted = true;
  private m_onChunk: ((base64Pcm16: string) => void) | null = null;
  private m_speechLevel = 0;

  get isCapturing(): boolean {
    return this.m_stream !== null;
  }

  /** Smoothed 0..1 input level for UI meters. */
  get inputLevel(): number {
    return this.m_speechLevel;
  }

  async startAsync(onChunk: (base64Pcm16: string) => void): Promise<boolean> {
    if (this.m_stream) {
      this.m_onChunk = onChunk;
      return true;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: K_REALTIME_MIC_CONSTRAINTS,
      });
      const context = new AudioContext({ sampleRate: REALTIME_SAMPLE_RATE });
      const workletUrl = URL.createObjectURL(
        new Blob([CAPTURE_WORKLET_SOURCE], { type: "application/javascript" }),
      );
      try {
        await context.audioWorklet.addModule(workletUrl);
      } finally {
        URL.revokeObjectURL(workletUrl);
      }

      const source = context.createMediaStreamSource(stream);
      const worklet = new AudioWorkletNode(context, "campfire-capture", {
        numberOfInputs: 1,
        numberOfOutputs: 0,
      });
      worklet.port.onmessage = (e: MessageEvent<Float32Array>) => {
        this.handleFrames(e.data, context.sampleRate);
      };
      source.connect(worklet);

      this.m_context = context;
      this.m_stream = stream;
      this.m_source = source;
      this.m_worklet = worklet;
      this.m_onChunk = onChunk;
      return true;
    } catch (err) {
      console.warn("[RealtimeMicCapture] microphone unavailable:", err);
      this.stop();
      return false;
    }
  }

  setMuted(muted: boolean): void {
    if (this.m_muted === muted) return;
    this.m_muted = muted;
    if (muted) {
      this.flushPending();
      this.appendSilence(MUTE_SILENCE_MS);
      this.m_speechLevel = 0;
    } else {
      void this.m_context?.resume();
    }
  }

  get isMuted(): boolean {
    return this.m_muted;
  }

  stop(): void {
    this.m_worklet?.port.close();
    this.m_worklet?.disconnect();
    this.m_source?.disconnect();
    this.m_stream?.getTracks().forEach((t) => t.stop());
    void this.m_context?.close().catch(() => undefined);
    this.m_worklet = null;
    this.m_source = null;
    this.m_stream = null;
    this.m_context = null;
    this.m_pending = [];
    this.m_onChunk = null;
    this.m_speechLevel = 0;
  }

  private handleFrames(frames: Float32Array, contextRate: number): void {
    if (this.m_muted || !this.m_onChunk) return;

    let level = 0;
    for (const sample of frames) level = Math.max(level, Math.abs(sample));
    this.m_speechLevel = this.m_speechLevel * 0.85 + level * 0.15;

    const resampled =
      contextRate === REALTIME_SAMPLE_RATE
        ? frames
        : linearResample(frames, contextRate, REALTIME_SAMPLE_RATE);
    for (const sample of resampled) this.m_pending.push(sample);
    if (this.m_pending.length >= MIC_FLUSH_SAMPLES) this.flushPending();
  }

  private flushPending(): void {
    if (this.m_pending.length === 0 || !this.m_onChunk) return;
    const chunk = this.m_pending;
    this.m_pending = [];
    this.m_onChunk(floatToPcm16Base64(chunk));
  }

  private appendSilence(ms: number): void {
    if (!this.m_onChunk) return;
    const samples = Math.round((ms / 1000) * REALTIME_SAMPLE_RATE);
    this.m_onChunk(floatToPcm16Base64(new Array<number>(samples).fill(0)));
  }
}

export class RealtimePcmPlayback {
  /** Fires on speaking-state transitions (Unity `AgentSpeakingStateChanged`
   *  source): true on first queued audio, false when the queue drains. */
  readonly onSpeakingChanged = new Emitter<boolean>();

  private m_context: AudioContext | null = null;
  private m_nextStartTime = 0;
  private m_activeSources = new Set<AudioBufferSourceNode>();
  private m_speakingState = false;
  private m_drainTimer: number | null = null;

  get isSpeaking(): boolean {
    const context = this.m_context;
    return !!context && this.m_nextStartTime > context.currentTime + 0.02;
  }

  enqueueBase64Pcm16(base64: string): void {
    const context = this.ensureContext();
    const float = pcm16Base64ToFloat(base64);
    if (float.length === 0) return;

    const buffer = context.createBuffer(1, float.length, REALTIME_SAMPLE_RATE);
    buffer.getChannelData(0).set(float);
    const source = context.createBufferSource();
    source.buffer = buffer;
    source.connect(context.destination);

    const startAt = Math.max(context.currentTime + 0.02, this.m_nextStartTime);
    source.start(startAt);
    this.m_nextStartTime = startAt + buffer.duration;
    this.m_activeSources.add(source);
    source.onended = () => this.m_activeSources.delete(source);
    this.setSpeakingState(true);
    this.scheduleDrainCheck();
  }

  /** Drop everything queued (Unity `StopOutputAudioPlayback`). */
  stopPlayback(): void {
    for (const source of this.m_activeSources) {
      try {
        source.stop();
      } catch {
        // already stopped
      }
    }
    this.m_activeSources.clear();
    this.m_nextStartTime = 0;
    this.clearDrainTimer();
    this.setSpeakingState(false);
  }

  private scheduleDrainCheck(): void {
    this.clearDrainTimer();
    const context = this.m_context;
    if (!context) return;
    const remainingMs = Math.max(0, (this.m_nextStartTime - context.currentTime) * 1000);
    this.m_drainTimer = window.setTimeout(() => {
      this.m_drainTimer = null;
      if (this.isSpeaking) {
        this.scheduleDrainCheck();
      } else {
        this.setSpeakingState(false);
      }
    }, remainingMs + 80);
  }

  private clearDrainTimer(): void {
    if (this.m_drainTimer !== null) {
      window.clearTimeout(this.m_drainTimer);
      this.m_drainTimer = null;
    }
  }

  private setSpeakingState(speaking: boolean): void {
    if (this.m_speakingState === speaking) return;
    this.m_speakingState = speaking;
    this.onSpeakingChanged.emit(speaking);
  }

  resumeIfNeeded(): void {
    void this.m_context?.resume();
  }

  dispose(): void {
    this.stopPlayback();
    this.clearDrainTimer();
    void this.m_context?.close().catch(() => undefined);
    this.m_context = null;
  }

  private ensureContext(): AudioContext {
    if (!this.m_context) {
      this.m_context = new AudioContext({ sampleRate: REALTIME_SAMPLE_RATE });
    }
    void this.m_context.resume();
    return this.m_context;
  }
}

// ---------------------------------------------------------------------------
// PCM16 <-> base64 helpers
// ---------------------------------------------------------------------------

function floatToPcm16Base64(samples: ArrayLike<number>): string {
  const pcm = new Int16Array(samples.length);
  for (let i = 0; i < samples.length; i++) {
    const clamped = Math.max(-1, Math.min(1, samples[i]));
    pcm[i] = Math.round(clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff);
  }
  const bytes = new Uint8Array(pcm.buffer);
  let binary = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

function pcm16Base64ToFloat(base64: string): Float32Array {
  let binary: string;
  try {
    binary = atob(base64);
  } catch {
    return new Float32Array(0);
  }
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  const pcm = new Int16Array(bytes.buffer, 0, Math.floor(bytes.length / 2));
  const float = new Float32Array(pcm.length);
  for (let i = 0; i < pcm.length; i++) {
    float[i] = pcm[i] / (pcm[i] < 0 ? 0x8000 : 0x7fff);
  }
  return float;
}

function linearResample(input: Float32Array, fromRate: number, toRate: number): Float32Array {
  if (fromRate === toRate || input.length === 0) return input;
  const ratio = fromRate / toRate;
  const outLength = Math.max(1, Math.floor(input.length / ratio));
  const output = new Float32Array(outLength);
  for (let i = 0; i < outLength; i++) {
    const pos = i * ratio;
    const i0 = Math.floor(pos);
    const i1 = Math.min(input.length - 1, i0 + 1);
    const frac = pos - i0;
    output[i] = input[i0] * (1 - frac) + input[i1] * frac;
  }
  return output;
}
